import { IconRegularChevronLeft, IconRegularChevronRight } from '@notino/react-styleguide'
import React, { useState } from 'react'

import useScrollableTabList from '../../../../hooks/useScrollableTabList'
import { SalonServicesResponse, ServiceCardCallbackData, ServiceDetailData, ServicesTopCategoryData } from '../../../../types/types'
import { getValidServiceCategories } from '../../../../utils/helper'
import * as SC from './SalonServicesTabsStyles'
import SalonServicesCards from '../SalonServicesCards/SalonServicesCards'
import { getServiceTabClickEvent } from '../../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../../utils/dataLayer'

type Props = {
	services: SalonServicesResponse
	onServiceCardButtonClick: (data: ServiceCardCallbackData) => void
	setServiceDetail: (data: ServiceDetailData) => void
	selectedTabKey: string | undefined
	phoneNumber: string
}

const getTabButtonId = (industryId: string) => `salon-services-tab-${industryId}`

const getTabPanelId = (industryId: string) => `salon-services-panel-${industryId}`

const SalonServicesTabs = ({ services, onServiceCardButtonClick, setServiceDetail, selectedTabKey, phoneNumber }: Props) => {
	const categories = getValidServiceCategories(services.groupedServicesByCategory)

	const [tabKey, setTabKey] = useState<string | undefined>(() => {
		if (categories.some((category) => category.id === selectedTabKey)) {
			return selectedTabKey
		}

		return categories[0]?.id
	})

	const selectCategory = (category: ServicesTopCategoryData) => {
		setTabKey(category.id)
		const event = getServiceTabClickEvent({ categoryId: category.id })
		pushToDataLayer(event)
	}

	const { scrollElementRef, isOverflown, isScrolledLeft, isScrolledRight, scrollLeft, scrollRight, handleTabKeyDown, handleTabFocus } = useScrollableTabList<
		ServicesTopCategoryData,
		HTMLUListElement
	>({
		selectedId: tabKey,
		items: categories,
		getTabButtonId,
		onSelect: selectCategory
	})

	const activeIndustry = services.groupedServicesByCategory.find((industry) => industry.category?.id === tabKey)

	return (
		<SC.ServicesTabsWrapper>
			<SC.TabsContainer>
				{isOverflown && isScrolledLeft && (
					<SC.ScrollLeftButton type='button' tabIndex={-1} aria-hidden={true} onClick={scrollLeft} style={{ zIndex: 10 }}>
						<IconRegularChevronLeft color={'icon.primary'} />
					</SC.ScrollLeftButton>
				)}
				<SC.Tabs role={'tablist'} ref={scrollElementRef}>
					{services.groupedServicesByCategory.map((industry) => {
						if (!industry.category) {
							return null
						}

						const { category } = industry
						const isActive = tabKey === category.id
						const tabId = getTabButtonId(category.id)
						const panelId = getTabPanelId(category.id)

						return (
							<SC.Tab key={category.id}>
								<SC.TabButton
									id={tabId}
									type='button'
									tabIndex={isActive ? 0 : -1}
									role={'tab'}
									aria-selected={isActive}
									aria-controls={panelId}
									onClick={() => selectCategory(category)}
									onKeyDown={(event) => handleTabKeyDown(event, category)}
									onFocus={handleTabFocus}
								>
									{category.name}
								</SC.TabButton>
							</SC.Tab>
						)
					})}
				</SC.Tabs>
				{isOverflown && isScrolledRight && (
					<SC.ScrollRightButton type='button' tabIndex={-1} aria-hidden={true} onClick={scrollRight} style={{ zIndex: 10 }}>
						<IconRegularChevronRight color={'icon.primary'} />
					</SC.ScrollRightButton>
				)}
			</SC.TabsContainer>
			<SC.TabsContent>
				{activeIndustry?.category && tabKey && (
					<SC.TabContent role='tabpanel' id={getTabPanelId(tabKey)} aria-labelledby={getTabButtonId(tabKey)}>
						<SalonServicesCards
							phoneNumber={phoneNumber}
							industryData={activeIndustry.category}
							onServiceCardButtonClick={onServiceCardButtonClick}
							setServiceDetail={setServiceDetail}
						/>
					</SC.TabContent>
				)}
			</SC.TabsContent>
		</SC.ServicesTabsWrapper>
	)
}

export default SalonServicesTabs
